import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Consultant, ConsultantContact, ManagedCustomer, AuthorizationFile } from '../types/consultant';

const COLLECTION_NAME = 'consultants';

/**
 * Összes szakreferens lekérése
 */
export const getAllConsultants = async (organizationId?: string): Promise<Consultant[]> => {
  try {
    let q = query(collection(db, COLLECTION_NAME), orderBy('companyName', 'asc'));
    
    if (organizationId) {
      q = query(
        collection(db, COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        orderBy('companyName', 'asc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
      managedCustomers: (doc.data().managedCustomers || []).map((mc: any) => ({
        ...mc,
        addedAt: mc.addedAt?.toDate() || new Date()
      })),
      authorizationFiles: (doc.data().authorizationFiles || []).map((af: any) => ({
        ...af,
        uploadedAt: af.uploadedAt?.toDate() || new Date()
      }))
    } as Consultant));
  } catch (error) {
    console.error('Error fetching consultants:', error);
    throw error;
  }
};

/**
 * Szakreferens lekérése ID alapján
 */
export const getConsultantById = async (id: string): Promise<Consultant | null> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      managedCustomers: (data.managedCustomers || []).map((mc: any) => ({
        ...mc,
        addedAt: mc.addedAt?.toDate() || new Date()
      })),
      authorizationFiles: (data.authorizationFiles || []).map((af: any) => ({
        ...af,
        uploadedAt: af.uploadedAt?.toDate() || new Date()
      }))
    } as Consultant;
  } catch (error) {
    console.error('Error fetching consultant:', error);
    throw error;
  }
};

/**
 * Új szakreferens létrehozása
 */
export const createConsultant = async (consultant: Omit<Consultant, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...consultant,
      createdAt: now,
      updatedAt: now,
      managedCustomers: consultant.managedCustomers.map(mc => ({
        ...mc,
        addedAt: Timestamp.fromDate(mc.addedAt)
      })),
      authorizationFiles: consultant.authorizationFiles.map(af => ({
        ...af,
        uploadedAt: Timestamp.fromDate(af.uploadedAt)
      }))
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating consultant:', error);
    throw error;
  }
};

/**
 * Szakreferens frissítése
 */
export const updateConsultant = async (id: string, consultant: Partial<Consultant>): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const updateData: any = {
      ...consultant,
      updatedAt: Timestamp.now()
    };

    // Convert dates to Timestamps
    if (consultant.managedCustomers) {
      updateData.managedCustomers = consultant.managedCustomers.map(mc => ({
        ...mc,
        addedAt: Timestamp.fromDate(mc.addedAt)
      }));
    }

    if (consultant.authorizationFiles) {
      updateData.authorizationFiles = consultant.authorizationFiles.map(af => ({
        ...af,
        uploadedAt: Timestamp.fromDate(af.uploadedAt)
      }));
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating consultant:', error);
    throw error;
  }
};

/**
 * Szakreferens törlése
 */
export const deleteConsultant = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting consultant:', error);
    throw error;
  }
};

/**
 * Kapcsolattartó hozzáadása szakreferenshez
 */
export const addContactToConsultant = async (consultantId: string, contact: ConsultantContact): Promise<void> => {
  try {
    const consultant = await getConsultantById(consultantId);
    if (!consultant) throw new Error('Consultant not found');

    const updatedContacts = [...consultant.contacts, contact];
    await updateConsultant(consultantId, { contacts: updatedContacts });
  } catch (error) {
    console.error('Error adding contact:', error);
    throw error;
  }
};

/**
 * Kapcsolattartó frissítése
 */
export const updateContactInConsultant = async (consultantId: string, contactId: string, updatedContact: Partial<ConsultantContact>): Promise<void> => {
  try {
    const consultant = await getConsultantById(consultantId);
    if (!consultant) throw new Error('Consultant not found');

    const updatedContacts = consultant.contacts.map(c => 
      c.id === contactId ? { ...c, ...updatedContact } : c
    );
    await updateConsultant(consultantId, { contacts: updatedContacts });
  } catch (error) {
    console.error('Error updating contact:', error);
    throw error;
  }
};

/**
 * Kapcsolattartó törlése
 */
export const deleteContactFromConsultant = async (consultantId: string, contactId: string): Promise<void> => {
  try {
    const consultant = await getConsultantById(consultantId);
    if (!consultant) throw new Error('Consultant not found');

    const updatedContacts = consultant.contacts.filter(c => c.id !== contactId);
    await updateConsultant(consultantId, { contacts: updatedContacts });
  } catch (error) {
    console.error('Error deleting contact:', error);
    throw error;
  }
};

/**
 * Ügyfél hozzáadása szakreferenshez
 */
export const addCustomerToConsultant = async (consultantId: string, customer: ManagedCustomer): Promise<void> => {
  try {
    const consultant = await getConsultantById(consultantId);
    if (!consultant) throw new Error('Consultant not found');

    const updatedCustomers = [...consultant.managedCustomers, customer];
    await updateConsultant(consultantId, { managedCustomers: updatedCustomers });
  } catch (error) {
    console.error('Error adding customer:', error);
    throw error;
  }
};

/**
 * Ügyfél törlése szakreferenstől
 */
export const removeCustomerFromConsultant = async (consultantId: string, customerId: string): Promise<void> => {
  try {
    const consultant = await getConsultantById(consultantId);
    if (!consultant) throw new Error('Consultant not found');

    const updatedCustomers = consultant.managedCustomers.filter(c => c.customerId !== customerId);
    await updateConsultant(consultantId, { managedCustomers: updatedCustomers });
  } catch (error) {
    console.error('Error removing customer:', error);
    throw error;
  }
};

/**
 * Meghatalmazás fájl hozzáadása
 */
export const addAuthorizationFile = async (consultantId: string, file: AuthorizationFile): Promise<void> => {
  try {
    const consultant = await getConsultantById(consultantId);
    if (!consultant) throw new Error('Consultant not found');

    const updatedFiles = [...consultant.authorizationFiles, file];
    await updateConsultant(consultantId, { authorizationFiles: updatedFiles });
  } catch (error) {
    console.error('Error adding authorization file:', error);
    throw error;
  }
};

/**
 * Meghatalmazás fájl törlése
 */
export const deleteAuthorizationFile = async (consultantId: string, fileId: string): Promise<void> => {
  try {
    const consultant = await getConsultantById(consultantId);
    if (!consultant) throw new Error('Consultant not found');

    const updatedFiles = consultant.authorizationFiles.filter(f => f.id !== fileId);
    await updateConsultant(consultantId, { authorizationFiles: updatedFiles });
  } catch (error) {
    console.error('Error deleting authorization file:', error);
    throw error;
  }
};

/**
 * Szakreferensek lekérése adott ügyfélhez
 */
export const getConsultantsByCustomerId = async (customerId: string): Promise<Consultant[]> => {
  try {
    const allConsultants = await getAllConsultants();
    return allConsultants.filter(consultant => 
      consultant.managedCustomers.some(mc => mc.customerId === customerId)
    );
  } catch (error) {
    console.error('Error fetching consultants by customer:', error);
    throw error;
  }
};
